import React, { type ReactElement } from 'react';
import { type GadgetData, GADGET_ERROR_TYPE } from '@atlassian/jira-dashboard-common';
import {
	GadgetAnalyticsContainer,
	GadgetBoundary,
	GadgetControllerContextConsumer,
	GadgetErrorFallback,
	GadgetErrorFallbackBase,
	GadgetInner,
	GadgetReadOnlyController,
	type useConnectGadgetTitles,
} from '@atlassian/jira-dashboard-internal-common';
import { Header } from './styled';

const noop = () => undefined;

type WallboardGadgetProps = {
	gadget: GadgetData;
	dashboardId: string;
	refreshId: number;
	unconfiguredFallback: ReactElement;
	onRegisterConnectGadget: ReturnType<typeof useConnectGadgetTitles>['registerConnectGadget'];
	onUnregisterConnectGadget: ReturnType<typeof useConnectGadgetTitles>['unregisterConnectGadget'];
};

export const WallboardGadget = ({
	gadget,
	dashboardId,
	refreshId,
	unconfiguredFallback,
	onRegisterConnectGadget,
	onUnregisterConnectGadget: onUnRegisterConnectGadget,
}: WallboardGadgetProps) => (
	<GadgetReadOnlyController display="normal" dashboardRefreshId={refreshId} data={gadget}>
		<GadgetControllerContextConsumer>
			{({
				isMaximized,
				shouldUseCache,
				isLoading,
				setIsLoading,
				isUnconfigured,
				setDefaultTitle,
				title,
				onRenameSave,
			}) => (
				<>
					<Header>{title}</Header>
					<GadgetBoundary
						data={gadget}
						isError={!gadget.title}
						errorFallback={
							gadget.errorMessage ? (
								<GadgetErrorFallbackBase id={gadget.id} message={gadget.errorMessage} />
							) : (
								<GadgetErrorFallback
									id={gadget.id}
									errorType={
										!gadget.title
											? GADGET_ERROR_TYPE.NOT_SUPPORTED_ERROR
											: GADGET_ERROR_TYPE.VIEW_ERROR
									}
								/>
							)
						}
					>
						<GadgetAnalyticsContainer data={gadget}>
							<GadgetInner
								dashboardId={dashboardId}
								data={gadget}
								isUnconfigured={isUnconfigured}
								isEditModeEnabled={false}
								isLoading={isLoading}
								isInEditMode={false}
								shouldUseCache={shouldUseCache}
								setIsLoading={setIsLoading}
								setDefaultTitle={setDefaultTitle}
								onEditModeCancel={noop}
								isMaximized={isMaximized}
								unconfiguredFallback={unconfiguredFallback}
								onRegisterConnectGadget={onRegisterConnectGadget}
								onUnregisterConnectGadget={onUnRegisterConnectGadget}
								setTitle={onRenameSave}
							/>
						</GadgetAnalyticsContainer>
					</GadgetBoundary>
				</>
			)}
		</GadgetControllerContextConsumer>
	</GadgetReadOnlyController>
);
